<template>
  <div class="vehicle-page-header">
    <div class="vehicle-page-header__content">
      <div class="vehicle-page-header__title-wrap">
        <h1 class="vehicle-page-header__title">{{ title }}</h1>
      </div>

      <div class="vehicle-page-header__main">
        <div class="vehicle-page-header__nav row items-center justify-between">
          <div class="vehicle-page-header__actions row items-center">
            <CalculatorButton
              class="vehicle-page-header__nav-btn"
              :label="backNavLabel"
              outline
              rounded
              iconDirection="left"
              :hasChevronIcon="showHeaderNavChevrons"
              color="primary"
              @click="$emit('back')"
            />
            <CalculatorButton
              class="vehicle-page-header__nav-btn"
              label="View shortlist"
              rounded
              iconDirection="right"
              :hasChevronIcon="showHeaderNavChevrons"
              :tertiaryBorder="true"
              :color="hasItemsInShortList ? 'tertiary' : undefined"
              :disabled="!hasItemsInShortList"
              @click="$emit('view-shortlist')"
            />
            <CalculatorButton
              class="vehicle-page-header__lease-options-toggle"
              type="button"
              label="Payment filters"
              rounded
              :icon="optionsIcon"
              iconDirection="right"
              outline
              color="primary"
              aria-label="Lease term and payments"
              @click="$emit('open-mobile-filters')"
            />
          </div>
          <div class="vehicle-page-header__filters row q-col-gutter-xl items-center">
            <RepaymentOptions
              :value="repayment"
              @input="$emit('update:repayment', $event)"
              :isActive="true"
              :labelPosition="filtersLabelPosition"
              label-styles="text-weight-light"
            />
            <LeaseTermOptions
              :value="leaseTerm"
              :options="leaseTermOptions"
              @input="$emit('update:leaseTerm', $event)"
              :isActive="true"
              :labelPosition="filtersLabelPosition"
              label-styles="text-weight-light"
            />
          </div>
        </div>

        <div class="row vehicle-page-header__row">
          <div class="col-12 col-lg-6 vehicle-page-header__col-left">
            <div class="vehicle-page-header__frame">
              <div class="vehicle-page-header__product-info">
                <div class="vehicle-page-header__car-details">
                  <p class="vehicle-page-header__meta text-grey-8">{{ product.meta }}</p>
                  <h2 class="vehicle-page-header__vehicle-title text-secondary">
                    {{ product.title }}{{ product.driveType ? ` ${product.driveType}` : '' }}
                  </h2>
                  <p class="vehicle-page-header__vehicle-desc text-grey-8">{{ product.subtitle }}</p>
                </div>
                <div class="vehicle-page-header__payment-details">
                  <button
                    type="button"
                    class="vehicle-page-header__payment-label-btn text-grey-8"
                    @click.stop="showEstimatedPaymentsModal = true"
                  >
                    <span>{{ pbd_cardTotalLabel }}</span>
                    <QIcon :name="infoIcon" size="20px" class="vehicle-page-header__info-icon" />
                  </button>
                  <div class="vehicle-page-header__cost">
                    <div class="vehicle-page-header__cost-stack">
                      <div class="vehicle-page-header__cost-row">
                        <span class="vehicle-page-header__price text-tertiary">{{ product.estimatedPrice }}</span>
                        <PaymentBreakdownButton
                          v-if="pbd_isPaymentBreakdownsEnabled"
                          :vehicle="item"
                          :pay-cycle="repayment"
                          @click="showPaymentBreakdownModal = true"
                        />
                      </div>
                    </div>
                    <div class="vehicle-page-header__rrp-row">
                      <span class="vehicle-page-header__rrp text-grey-8">RRP: {{ product.rrp }}</span>
                      <QIcon
                        :name="infoIcon"
                        size="20px"
                        class="vehicle-page-header__info-icon cursor-pointer"
                        @click="showInformationModal = true"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="col-12 col-lg-6 vehicle-page-header__col-right">
            <div class="vehicle-page-header__image-wrap">
              <QImg
                :src="product.imageUrl"
                ratio="16/10"
                contain
                class="vehicle-page-header__image"
                :placeholder-src="placeholderImageSvg"
              />
            </div>
            <p class="vehicle-page-header__disclaimer">Vehicle image shown may differ from actual model.</p>
          </div>
        </div>
      </div>
    </div>

    <div class="vehicle-page-header__pagination">
      <div class="row items-center justify-between vehicle-page-header__pagination-inner">
        <div class="vehicle-page-header__ctas">
          <CalculatorButton
            color="primary"
            label="Enquire now"
            rounded
            class="vehicle-page-header__cta-primary"
            @click="$emit('enquire-now')"
          />
          <div class="vehicle-page-header__ctas-row">
            <CalculatorButton
              :label="isShortListed ? 'Shortlisted' : 'Shortlist'"
              rounded
              tertiaryBorder
              :disabled="!isShortListed && isShortlistAtMax"
              :color="isShortListed ? 'tertiary' : undefined"
              @click="$emit('shortlist')"
            />
            <CalculatorButton
              v-if="variants && variants.length > 0"
              :label="viewVariantsBtnLabel"
              rounded
              tertiaryBorder
              @click="toggleVariantsSection"
            />
          </div>
        </div>
        <div v-if="1 === 0" class="vehicle-page-header__pagination-dots">
          <span
            v-for="(_, i) in paginationTotal"
            :key="i"
            class="vehicle-page-header__dot"
            :class="{ 'vehicle-page-header__dot--active': i === currentSlide }"
          />
        </div>
      </div>
    </div>

    <QSlideTransition>
      <div class="vehicle-page-header__variants-wrap">
        <VehicleVariants
          :key="variantsSectionKey"
          v-show="showVariants"
          class="q-mt-lg"
          :variants="variants"
          :short-list="shortList"
          :repayment="repayment"
          :title="product.title"
          @close="$emit('close-variants')"
          @select-variant="onSelectVariant"
          @on-view-vehicle="$emit('on-view-vehicle', $event)"
          @on-shortlist="$emit('on-shortlist', $event)"
        />
      </div>
    </QSlideTransition>

    <EstimatedPaymentsModal
      v-model="showEstimatedPaymentsModal"
      @onClose="showEstimatedPaymentsModal = false"
      @request-quote="$emit('enquire-now')"
    />
    <PaymentBreakdown
      v-if="pbd_isPaymentBreakdownsEnabled"
      v-model="showPaymentBreakdownModal"
      :vehicle-finance-amount="pbd_cardParts ? pbd_cardParts.finance : ''"
      :bundled-running-costs-amount="pbd_cardParts ? pbd_cardParts.bundled : ''"
      :estimated-payment="pbd_cardEstimatedPayment"
      :payment-total-label="pbd_cardTotalLabel"
      :payment-cadence="paymentBreakdownModalCadence"
      @onClose="showPaymentBreakdownModal = false"
    />
    <InformationModal v-model="showInformationModal" @onClose="showInformationModal = false" />
  </div>
</template>

<script>
import { QIcon, QImg, QSlideTransition } from 'quasar';
import CalculatorButton from '../CalculatorButton';
import RepaymentOptions from '../RepaymentOptions';
import LeaseTermOptions from '../LeaseTermOptions';
import PaymentBreakdownButton from '../PaymentBreakdownButton.vue';
import { evaInfoOutline, evaOptions2Outline } from '@quasar/extras/eva-icons';
import EstimatedPaymentsModal from '../Modal/EstimatedPaymentsModal.vue';
import PaymentBreakdown from '../Modal/PaymentBreakdown.vue';
import InformationModal from '../Modal/InformationModal.vue';
import VehicleVariants from './VehicleVariants.vue';
import { formatVehicleDescription } from '../../../utils/calculator/vehicleFormatter';
import { formatPaymentBreakdownAmount } from '../../../utils/calculator/ratebookPaymentBreakdownFields';
import { PaymentBreakdownMixin } from '../../../mixins/calculator';
import { REPAYMENT_WEEKLY } from '../../../utils/constants';

/** Align with `$vp-mobile-max` in `vehicle-product-page.sass` (nav button compact styles). */
const VEHICLE_HEADER_MOBILE_MAX_PX = 767;

const EMPTY_PRODUCT = {
  meta: '',
  title: '',
  driveType: '',
  subtitle: '',
  estimatedPrice: '',
  rrp: '',
  imageUrl: ''
};

export default {
  name: 'VehiclePageHeader',
  mixins: [PaymentBreakdownMixin],
  components: {
    QIcon,
    QImg,
    QSlideTransition,
    CalculatorButton,
    RepaymentOptions,
    LeaseTermOptions,
    PaymentBreakdownButton,
    EstimatedPaymentsModal,
    PaymentBreakdown,
    InformationModal,
    VehicleVariants
  },
  props: {
    title: {
      type: String,
      default: 'Lease this vehicle for your business'
    },
    /** Ratebook item (current vehicle); product is derived from this */
    item: {
      type: Object,
      default: null
    },
    repayment: {
      type: String,
      default: REPAYMENT_WEEKLY
    },
    leaseTerm: {
      type: Object,
      default: null
    },
    leaseTermOptions: {
      type: Array,
      default: () => []
    },
    placeholderImageSvg: {
      type: String,
      default: null
    },
    backToSearchRoute: {
      type: Object,
      default: () => ({ path: '/calculator/results' })
    },
    backNavLabel: {
      type: String,
      default: 'Back to search'
    },
    paginationTotal: {
      type: Number,
      default: 4
    },
    currentSlide: {
      type: Number,
      default: 0
    },
    variants: {
      type: Array,
      default: () => []
    },
    showVariants: {
      type: Boolean,
      default: false
    },
    hasItemsInShortList: {
      type: Boolean,
      default: false
    },
    shortList: {
      type: Array,
      default: () => []
    },
    variantsSectionKey: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      showEstimatedPaymentsModal: false,
      showPaymentBreakdownModal: false,
      showInformationModal: false
    };
  },
  computed: {
    /** Required by PaymentBreakdownMixin (`pbd_card*`) */
    displayWeeklyPrice() {
      return this.repayment === REPAYMENT_WEEKLY;
    },
    weeklyPrice() {
      return this.item?.weekLeaseprice ?? null;
    },
    monthlyPrice() {
      return this.item?.monthLeaseprice ?? null;
    },
    /** Required by PaymentBreakdownMixin (`pbd_cardParts`) — `item` carries the four backend fields. */
    pbd_ratebookRow() {
      return this.item;
    },
    product() {
      const i = this.item;
      if (!i) return EMPTY_PRODUCT;
      const estimatedPrice = this.repayment === REPAYMENT_WEEKLY ? i.weekLeaseprice : i.monthLeaseprice;
      return {
        meta: [i.catalogYear, i.vehicleFueltype, i.vehicleTransmissiongroup].filter(Boolean).join(' | '),
        title: [i.vehicleMake, i.vehicleModel, i.vehicleTrim].filter(Boolean).join(' '),
        driveType: i.vehicleDrivetrain ? `| ${i.vehicleDrivetrain}` : '',
        subtitle: formatVehicleDescription(i)?.trim() || '',
        estimatedPrice: formatPaymentBreakdownAmount(estimatedPrice),
        rrp: formatPaymentBreakdownAmount(i.consumerPrice),
        imageUrl: i.jatoImagepathTrim || i.jatoImagepath || ''
      };
    },
    paymentBreakdownModalCadence() {
      return this.repayment === REPAYMENT_WEEKLY ? 'weekly' : 'monthly';
    },
    viewVariantsBtnLabel() {
      return this.showVariants ? 'Close variants' : 'View variants';
    },
    filtersLabelPosition() {
      return this.$q.screen.lt.md ? 'top' : 'left';
    },
    /** Chevron arrows on back / shortlist; hidden at mobile widths. */
    showHeaderNavChevrons() {
      const w = this.$q.screen.width;
      if (!w) return true;
      return w > VEHICLE_HEADER_MOBILE_MAX_PX;
    },
    isShortListed() {
      return this.shortList.some((item) => item.catalogVehicleId === this.item?.catalogVehicleId);
    },
    isShortlistAtMax() {
      const MAX_SHORTLIST_COUNT = 3;
      return this.shortList.length >= MAX_SHORTLIST_COUNT;
    }
  },
  methods: {
    toggleVariantsSection() {
      if (this.showVariants) {
        this.$emit('close-variants');
      } else {
        this.$emit('view-variants');
      }
    },
    onSelectVariant(variant) {
      this.$emit('select-variant', variant);
    }
  },
  created() {
    this.infoIcon = evaInfoOutline;
    this.optionsIcon = evaOptions2Outline;
  }
};
</script>
